import { AlertCircle, AlertTriangle, CheckCircle } from "lucide-react";
import React from "react";
import { cn } from "src/common/utils";

type StatusAlertVariant = "warning" | "error" | "success";

const variantStyles: Record<StatusAlertVariant, { box: string; icon: string }> = {
  warning: {
    box: "bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-600/30 dark:text-yellow-200",
    icon: "text-yellow-600 dark:text-yellow-500",
  },
  error: {
    box: "bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300",
    icon: "text-red-600 dark:text-red-400",
  },
  success: {
    box: "bg-green-50 border-green-200 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300",
    icon: "text-green-600 dark:text-green-400",
  },
};

const defaultIcons: Record<StatusAlertVariant, React.FC<{ className?: string }>> = {
  warning: AlertCircle,
  error: AlertCircle,
  success: CheckCircle,
};

interface StatusAlertProps {
  children: React.ReactNode;
  variant?: StatusAlertVariant;
  size?: "sm" | "default";
  icon?: "circle" | "triangle" | "none";
  className?: string;
}

export const StatusAlert: React.FC<StatusAlertProps> = ({
  children,
  variant = "warning",
  size = "default",
  icon,
  className,
}) => {
  const isSmall = size === "sm";
  const styles = variantStyles[variant];
  const showIcon = icon !== "none";
  const Icon = icon === "triangle" ? AlertTriangle : defaultIcons[variant];

  return (
    <div
      className={cn(
        "flex items-start rounded border",
        styles.box,
        isSmall ? "gap-1.5 p-2 text-xs" : "gap-2 p-3 text-sm rounded-lg",
        className,
      )}
    >
      {showIcon && (
        <Icon
          className={cn(
            "flex-shrink-0 mt-0.5",
            styles.icon,
            isSmall ? "h-3 w-3" : "h-4 w-4",
          )}
        />
      )}
      <div>{children}</div>
    </div>
  );
};

/** Convenience aliases */
export const WarningAlert: React.FC<Omit<StatusAlertProps, "variant">> = (props) => (
  <StatusAlert variant="warning" {...props} />
);

export const ErrorAlert: React.FC<Omit<StatusAlertProps, "variant">> = (props) => (
  <StatusAlert variant="error" {...props} />
);

export const SuccessAlert: React.FC<Omit<StatusAlertProps, "variant">> = (props) => (
  <StatusAlert variant="success" {...props} />
);
